import {
  ChevronLeft,
  ChevronRight,
  GripVertical,
  Images,
  Loader2,
  TriangleAlert,
} from "lucide-react";
import {
  useMemo,
  useState,
  type CSSProperties,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useLocation, useNavigate } from "react-router";
import { toast } from "sonner";

import {
  buildInlineChatGeneratedMediaItems,
  getStartedCampaignGenerationSkeletonCount,
  groupChatGeneratedImageBatchesByThread,
  groupChatGeneratedPostAssets,
  shouldPollChatGeneratedAssets,
  shouldPollChatGeneratedImages,
} from "@/lib/assistant-chat-media-gallery";
import type { StartedAssistantCampaignGeneration } from "@/lib/assistant-campaign-generation-previews";
import { buildFocusedPostEditorPath } from "@/lib/focused-post-editor";
import { buildBrandPath } from "@/hooks/use-route-brand";
import { getScheduledPostOutput, resolveScheduledPostPreview } from "@/lib/scheduled-post-preview";
import { applySocialPostVariantDraft } from "@/lib/social-post-variant-drafts";
import { trpc } from "@/lib/trpc";
import { cn } from "@/lib/utils";
import { createSocialPostEditableDraft, socialPostEditableDraftSchema } from "@runcheetah/shared";
import { Badge, Button } from "@runcheetah/ui";

import {
  ChatGeneratedAssetSkeletonCard,
  ChatGeneratedImageCard,
  ChatGeneratedPostAssetVariantCard,
} from "./assistant-chat-media-gallery-cards";
import type {
  ChatGeneratedAsset,
  ChatGeneratedAssetBatch,
  ChatGeneratedImage,
  ChatGeneratedImageBatch,
} from "./assistant-chat-media-gallery-types";
import { clampPanelWidth } from "./assistant-chat-media-gallery-view-model";

const PENDING_CHAT_ID = "00000000-0000-4000-8000-000000000000";
const EMPTY_GENERATED_ASSET_BATCHES: ChatGeneratedAssetBatch[] = [];
const EMPTY_GENERATED_ASSETS: ChatGeneratedAsset[] = [];
const EMPTY_GENERATED_IMAGE_BATCHES: ChatGeneratedImageBatch[] = [];
const EMPTY_GENERATED_IMAGES: ChatGeneratedImage[] = [];
const DEFAULT_PANEL_WIDTH = 480;
const INLINE_MEDIA_GALLERY_COLUMN_COUNT = 2;
type ChatGeneratedAssetPost = NonNullable<ChatGeneratedAsset["post"]>;
type ChatGeneratedAssetDraft = NonNullable<ChatGeneratedAssetPost["draft"]>;

function buildDraftForGeneratedAssetSelection(
  asset: ChatGeneratedAsset,
): ChatGeneratedAssetDraft | null {
  if (!asset.post) {
    return null;
  }

  const output = getScheduledPostOutput(asset.post.output);
  const preview = resolveScheduledPostPreview({
    output: asset.post.output,
    draft: null,
    selectedCarouselSlideId: asset.slideId,
    selectedCarouselSlideIndex: asset.slideIndex,
  });
  const carouselSlides =
    output.latestCompleted?.carousel?.slides.flatMap((slideOutput) => {
      const plan = slideOutput.scenePlan?.plan ?? null;
      return plan
        ? [
            {
              slideId: slideOutput.slide.id,
              slideIndex: slideOutput.slide.index,
              plan,
              imageAssetOverrides: [],
            },
          ]
        : [];
    }) ?? [];

  if (asset.post.draft) {
    const draft = applySocialPostVariantDraft(asset.post.draft, asset.generatedAssetId);

    if (carouselSlides.length === 0) {
      return draft;
    }

    const existingSlideDraftById = new Map(
      draft.carouselSlides.map((slide) => [slide.slideId, slide] as const),
    );
    const existingSlideDraftByIndex = new Map(
      draft.carouselSlides.map((slide) => [slide.slideIndex, slide] as const),
    );

    return socialPostEditableDraftSchema.parse({
      ...draft,
      plan: carouselSlides[0]?.plan ?? draft.plan,
      carouselSlides: carouselSlides.map(
        (slide) =>
          existingSlideDraftById.get(slide.slideId) ??
          existingSlideDraftByIndex.get(slide.slideIndex) ??
          slide,
      ),
    });
  }

  const plan = output.latestCompleted?.result?.plan ?? carouselSlides[0]?.plan ?? preview.plan;

  if (!plan) {
    return null;
  }

  return createSocialPostEditableDraft({
    plan,
    caption: preview.caption || null,
    hashtags: preview.hashtags,
    carouselSlides,
  });
}

function isAssetCarouselSlideDraft(
  slide: ChatGeneratedAssetDraft["carouselSlides"][number],
  asset: ChatGeneratedAsset,
) {
  return slide.slideId === asset.slideId || slide.slideIndex === asset.slideIndex;
}

export function AssistantChatMediaGalleryPanel({
  brandId,
  chatId,
  enabled,
  startedCampaignGenerations = [],
  profileLabel,
  className,
  onUseAssetInChat,
  onChangeAssetCopyInChat,
  onUseImageInChat,
  onUseImageAsVisualUniverseSeed,
}: {
  brandId: string;
  chatId: string | null;
  enabled: boolean;
  startedCampaignGenerations?: StartedAssistantCampaignGeneration[];
  profileLabel: string;
  className?: string;
  onUseAssetInChat?: (asset: ChatGeneratedAsset) => void;
  onChangeAssetCopyInChat?: (asset: ChatGeneratedAsset) => void;
  onUseImageInChat?: (image: ChatGeneratedImage) => void;
  onUseImageAsVisualUniverseSeed?: (image: ChatGeneratedImage) => void;
}) {
  const intl = useIntl();
  const navigate = useNavigate();
  const location = useLocation();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [panelWidth, setPanelWidth] = useState(DEFAULT_PANEL_WIDTH);
  const [primaryAssetPendingId, setPrimaryAssetPendingId] = useState<string | null>(null);
  const utils = trpc.useUtils();
  const query = trpc.brandAssistantChat.generatedAssets.useQuery(
    { brandId, chatId: chatId ?? PENDING_CHAT_ID },
    {
      enabled: enabled && Boolean(chatId),
      refetchOnWindowFocus: true,
      refetchInterval: (refetchQuery) =>
        enabled &&
        chatId &&
        (shouldPollChatGeneratedAssets(refetchQuery.state.data) ||
          startedCampaignGenerations.length > 0)
          ? 5_000
          : false,
    },
  );
  const imagesQuery = trpc.brandAssistantChat.generatedImages.useQuery(
    { brandId, chatId: chatId ?? PENDING_CHAT_ID },
    {
      enabled: enabled && Boolean(chatId),
      refetchOnWindowFocus: true,
      refetchInterval: (refetchQuery) =>
        enabled && chatId && shouldPollChatGeneratedImages(refetchQuery.state.data) ? 5_000 : false,
    },
  );
  const campaignGenerationCheckQuery = trpc.campaign.nextGenerationCheck.useQuery(
    { brandId },
    {
      enabled: enabled && startedCampaignGenerations.length > 0,
      refetchInterval: (refetchQuery) =>
        enabled && refetchQuery.state.data?.mode === "on_demand" ? 5_000 : false,
    },
  );
  const logosQuery = trpc.brand.listLogos.useQuery(
    { brandId },
    { enabled: enabled && Boolean(chatId), staleTime: 0 },
  );
  const logoAssets = useMemo(
    () =>
      (logosQuery.data?.logos ?? []).map((logo) => ({
        objectKey: logo.objectKey,
        signedUrl: logo.signedUrl,
        isPrimary: logo.isPrimary,
      })),
    [logosQuery.data?.logos],
  );
  const approvePost = trpc.calendar.approve.useMutation();
  const rejectPost = trpc.calendar.reject.useMutation();
  const savePostDraft = trpc.standaloneAssetBatch.upsertPostDraft.useMutation();
  const saveCampaignPostDraft = trpc.campaign.upsertPostDraft.useMutation();
  const saveGeneratedImage = trpc.brandAssistantChat.saveGeneratedImageToMediaLibrary.useMutation();
  const batches = query.data?.batches ?? EMPTY_GENERATED_ASSET_BATCHES;
  const assets = query.data?.assets ?? EMPTY_GENERATED_ASSETS;
  const imageBatches = imagesQuery.data?.batches ?? EMPTY_GENERATED_IMAGE_BATCHES;
  const images = imagesQuery.data?.images ?? EMPTY_GENERATED_IMAGES;
  const postAssetGroups = useMemo(
    () => groupChatGeneratedPostAssets({ batches, assets }),
    [assets, batches],
  );
  const imageBatchThreads = useMemo(
    () => groupChatGeneratedImageBatchesByThread({ batches: imageBatches, images }),
    [imageBatches, images],
  );
  const pendingPostBatches = useMemo(
    () => batches.filter((batch) => batch.pendingTaskCount > 0),
    [batches],
  );
  const inlineMediaItems = useMemo(
    () =>
      buildInlineChatGeneratedMediaItems({
        postAssetGroups,
        imageBatchThreads,
        pendingPostBatches,
      }),
    [imageBatchThreads, pendingPostBatches, postAssetGroups],
  );
  const startedCampaignSkeletonItems = useMemo(() => {
    const hasCampaignBatch = batches.some((batch) => batch.sourceType === "campaign");
    const shouldShowStartedCampaigns =
      startedCampaignGenerations.length > 0 &&
      !hasCampaignBatch &&
      (campaignGenerationCheckQuery.isPending ||
        campaignGenerationCheckQuery.data?.mode === "on_demand");

    if (!shouldShowStartedCampaigns) {
      return [];
    }

    return startedCampaignGenerations.flatMap((campaign) =>
      Array.from({ length: getStartedCampaignGenerationSkeletonCount(campaign) }, (_, index) => ({
        kind: "pending" as const,
        key: `campaign-pending:${campaign.state.workerJobId}:${index}`,
        createdAt: campaign.firstScheduledFor ?? campaign.request.items[0]?.scheduledFor ?? "",
      })),
    );
  }, [
    batches,
    campaignGenerationCheckQuery.data?.mode,
    campaignGenerationCheckQuery.isPending,
    startedCampaignGenerations,
  ]);
  const visibleInlineMediaItems = useMemo(
    () => [...startedCampaignSkeletonItems, ...inlineMediaItems],
    [inlineMediaItems, startedCampaignSkeletonItems],
  );
  const inlineMediaColumns = useMemo(
    () => buildRowFirstMediaGalleryColumns(visibleInlineMediaItems),
    [visibleInlineMediaItems],
  );
  const failedTaskCount = batches.reduce((sum, batch) => sum + batch.failedTaskCount, 0);
  const failedImageBatchCount = imageBatches.filter((batch) => batch.status === "failed").length;
  const shouldShowPanel =
    visibleInlineMediaItems.length > 0 || imageBatches.length > 0 || failedTaskCount > 0;
  const panelStyle = {
    "--pulse-media-gallery-panel-width": `${panelWidth}px`,
  } as CSSProperties;

  const startResize = (event: ReactPointerEvent<HTMLButtonElement>) => {
    event.preventDefault();
    const startX = event.clientX;
    const startWidth = panelWidth;
    const previousCursor = document.body.style.cursor;
    const previousUserSelect = document.body.style.userSelect;
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";

    const handlePointerMove = (moveEvent: PointerEvent) => {
      setPanelWidth(clampPanelWidth(startWidth + startX - moveEvent.clientX));
    };
    const handlePointerUp = () => {
      document.body.style.cursor = previousCursor;
      document.body.style.userSelect = previousUserSelect;
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
    };

    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", handlePointerUp, { once: true });
  };

  const openAsset = (asset: ChatGeneratedAsset) => {
    if (!asset.scheduledPostId) {
      return;
    }

    navigate(
      buildFocusedPostEditorPath({
        brandId,
        owner: { type: asset.batchSourceType, id: asset.batchId },
        postId: asset.scheduledPostId,
      }),
      {
        state: {
          backgroundLocation: location,
          focusedEditorSelection: {
            blockId: asset.blockId,
            generatedAssetId: asset.generatedAssetId,
            slideId: asset.slideId,
            slideIndex: asset.slideIndex,
            asset: {
              generatedAssetId: asset.generatedAssetId,
              blockId: asset.blockId,
              signedUrl: asset.signedUrl,
              width: asset.width,
              height: asset.height,
            },
          },
        },
      },
    );
  };

  const invalidateReviewState = async (asset: ChatGeneratedAsset) => {
    await Promise.all([
      chatId
        ? utils.brandAssistantChat.generatedAssets.invalidate({
            brandId,
            chatId,
          })
        : Promise.resolve(),
      utils.calendar.list.invalidate(),
      asset.batchSourceType === "campaign"
        ? utils.campaign.get.invalidate({ brandId, id: asset.batchId })
        : utils.standaloneAssetBatch.get.invalidate({ brandId, id: asset.batchId }),
    ]);
  };

  const reviewAsset = (asset: ChatGeneratedAsset, approvalStatus: "approved" | "rejected") => {
    if (!asset.scheduledPostId) {
      return;
    }

    const mutation = approvalStatus === "approved" ? approvePost : rejectPost;
    mutation.mutate(
      { brandId, scheduledPostId: asset.scheduledPostId },
      {
        onSuccess: async () => {
          await invalidateReviewState(asset);
        },
        onError: (mutationError) => {
          toast.error(mutationError.message);
        },
      },
    );
  };

  const useAssetAsMainImage = (asset: ChatGeneratedAsset) => {
    const draft = buildDraftForGeneratedAssetSelection(asset);
    if (!asset.scheduledPostId || !asset.post || !draft) {
      toast.error(
        intl.formatMessage({
          id: "pulse.mediaGallery.mainImageUnavailable",
          defaultMessage: "This image cannot be set as the main image.",
        }),
      );
      return;
    }

    const override = {
      blockId: asset.blockId,
      generatedAssetId: asset.generatedAssetId,
    };
    const updateOverrides = (overrides: ChatGeneratedAssetDraft["imageAssetOverrides"]) => [
      ...overrides.filter((item) => item.blockId !== override.blockId),
      override,
    ];
    const isCarouselAsset = asset.slideId !== null || asset.slideIndex !== null;
    const nextDraft = isCarouselAsset
      ? {
          ...draft,
          carouselSlides: draft.carouselSlides.map((slide) =>
            isAssetCarouselSlideDraft(slide, asset)
              ? {
                  ...slide,
                  imageAssetOverrides: updateOverrides(slide.imageAssetOverrides),
                }
              : slide,
          ),
        }
      : {
          ...draft,
          imageAssetOverrides: updateOverrides(draft.imageAssetOverrides),
        };

    if (
      isCarouselAsset &&
      !draft.carouselSlides.some((slide) => isAssetCarouselSlideDraft(slide, asset))
    ) {
      toast.error(
        intl.formatMessage({
          id: "pulse.mediaGallery.mainImageUnavailable",
          defaultMessage: "This image cannot be set as the main image.",
        }),
      );
      return;
    }

    setPrimaryAssetPendingId(asset.generatedAssetId);
    const mutationOptions = {
      onSuccess: async () => {
        await Promise.all([
          chatId
            ? utils.brandAssistantChat.generatedAssets.invalidate({
                brandId,
                chatId,
              })
            : Promise.resolve(),
          utils.calendar.list.invalidate(),
          asset.batchSourceType === "campaign"
            ? utils.campaign.get.invalidate({ brandId, id: asset.batchId })
            : utils.standaloneAssetBatch.get.invalidate({
                brandId,
                id: asset.batchId,
              }),
        ]);
      },
      onError: (mutationError: { message: string }) => {
        toast.error(mutationError.message);
      },
      onSettled: () => {
        setPrimaryAssetPendingId(null);
      },
    };

    if (asset.batchSourceType === "campaign") {
      saveCampaignPostDraft.mutate(
        {
          brandId,
          campaignId: asset.batchId,
          scheduledPostId: asset.scheduledPostId,
          draft: nextDraft,
          expectedDraftUpdatedAt: asset.post.draftUpdatedAt ?? null,
        },
        mutationOptions,
      );
      return;
    }

    savePostDraft.mutate(
      {
        brandId,
        batchId: asset.batchId,
        scheduledPostId: asset.scheduledPostId,
        draft: nextDraft,
        expectedDraftUpdatedAt: asset.post.draftUpdatedAt ?? null,
      },
      mutationOptions,
    );
  };

  const saveImageToLibrary = (image: ChatGeneratedImage, enableAsReference: boolean) => {
    if (!chatId) {
      return;
    }

    saveGeneratedImage.mutate(
      {
        brandId,
        chatId,
        generatedAssetId: image.generatedAssetId,
        displayName: image.prompt?.slice(0, 120) || undefined,
        enableAsReference,
      },
      {
        onSuccess: async () => {
          await Promise.all([
            utils.brandAssistantChat.generatedImages.invalidate({
              brandId,
              chatId,
            }),
            utils.mediaGallery.list.invalidate(),
          ]);
          toast.success(
            enableAsReference
              ? intl.formatMessage({
                  id: "pulse.mediaGallery.imageSavedAsReference",
                  defaultMessage: "Saved to media library and enabled as a reference.",
                })
              : intl.formatMessage({
                  id: "pulse.mediaGallery.imageSaved",
                  defaultMessage: "Saved to media library.",
                }),
          );
        },
        onError: (mutationError) => {
          toast.error(mutationError.message);
        },
      },
    );
  };

  if (!shouldShowPanel) {
    return null;
  }

  return (
    <>
      <aside
        className={cn(
          "relative flex w-full shrink-0 flex-col border-t border-border/70 bg-card/88 backdrop-blur-xl transition-[max-height,width] duration-200 lg:h-full lg:max-h-none lg:border-l lg:border-t-0",
          isCollapsed
            ? "max-h-16 lg:w-14"
            : "max-h-[42rem] lg:w-[var(--pulse-media-gallery-panel-width)]",
          className,
        )}
        style={panelStyle}
      >
        {!isCollapsed ? (
          <button
            type="button"
            className="absolute left-0 top-1/2 z-10 hidden h-14 w-2 -translate-y-1/2 cursor-col-resize items-center justify-center rounded-r-full bg-foreground/10 text-muted-foreground/60 transition hover:w-3 hover:bg-foreground/15 hover:text-foreground lg:flex"
            onPointerDown={startResize}
            aria-label={intl.formatMessage({
              id: "pulse.mediaGallery.resizePanel",
              defaultMessage: "Resize media gallery panel",
            })}
          >
            <GripVertical className="h-3 w-3" aria-hidden />
          </button>
        ) : null}

        {isCollapsed ? (
          <button
            type="button"
            className="flex h-16 w-full items-center justify-center gap-2 px-3 text-sm font-semibold text-foreground transition hover:bg-background/65 lg:h-full lg:flex-col lg:px-0"
            onClick={() => setIsCollapsed(false)}
          >
            <ChevronLeft className="h-4 w-4" aria-hidden />
            <span className="lg:[writing-mode:vertical-rl]">
              <FormattedMessage id="pulse.mediaGallery.collapsedTitle" defaultMessage="Media" />
            </span>
          </button>
        ) : (
          <>
            <div className="border-b border-border/60 px-4 py-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                    <FormattedMessage
                      id="pulse.mediaGallery.eyebrow"
                      defaultMessage="Media gallery"
                    />
                  </p>
                </div>
                <div className="flex items-center gap-1.5">
                  {query.isFetching ||
                  imagesQuery.isFetching ||
                  campaignGenerationCheckQuery.isFetching ? (
                    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" aria-hidden />
                  ) : null}
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-8 rounded-full px-2 text-muted-foreground hover:text-foreground"
                    onClick={() => setIsCollapsed(true)}
                    aria-label={intl.formatMessage({
                      id: "pulse.mediaGallery.collapsePanel",
                      defaultMessage: "Collapse media gallery panel",
                    })}
                  >
                    <ChevronRight className="h-4 w-4" aria-hidden />
                  </Button>
                </div>
              </div>

              {failedTaskCount > 0 || failedImageBatchCount > 0 ? (
                <div className="mt-3 flex flex-wrap gap-1.5">
                  <Badge
                    variant="outline"
                    className="gap-1 rounded-full border-destructive/30 text-destructive"
                  >
                    <TriangleAlert className="h-3 w-3" aria-hidden />
                    <FormattedMessage
                      id="pulse.mediaGallery.failedCount"
                      defaultMessage="{count} failed"
                      values={{
                        count: failedTaskCount + failedImageBatchCount,
                      }}
                    />
                  </Badge>
                </div>
              ) : null}
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto p-3">
              {!enabled || !chatId ? (
                <MediaGalleryEmptyState>
                  <FormattedMessage
                    id="pulse.mediaGallery.waitingForChat"
                    defaultMessage="Generated media from this chat will appear here."
                  />
                </MediaGalleryEmptyState>
              ) : query.isPending || imagesQuery.isPending ? (
                <MediaGalleryLoadingState />
              ) : query.isError || imagesQuery.isError ? (
                <MediaGalleryEmptyState tone="error">
                  <FormattedMessage
                    id="pulse.mediaGallery.loadFailed"
                    defaultMessage="Could not load generated media: {message}"
                    values={{
                      message: query.error?.message ?? imagesQuery.error?.message,
                    }}
                  />
                </MediaGalleryEmptyState>
              ) : visibleInlineMediaItems.length > 0 ? (
                <div className="grid grid-cols-2 items-start gap-2.5">
                  {inlineMediaColumns.map((column, columnIndex) => (
                    <div key={columnIndex} className="flex min-w-0 flex-col gap-2.5">
                      {column.map((item) => {
                        if (item.kind === "pending") {
                          return <ChatGeneratedAssetSkeletonCard key={item.key} />;
                        }

                        if (item.kind === "image") {
                          return (
                            <ChatGeneratedImageCard
                              key={item.key}
                              image={item.image}
                              savePending={
                                saveGeneratedImage.isPending &&
                                saveGeneratedImage.variables?.generatedAssetId ===
                                  item.image.generatedAssetId
                              }
                              onUseInChat={() => onUseImageInChat?.(item.image)}
                              onUseAsVisualUniverseSeed={
                                onUseImageAsVisualUniverseSeed
                                  ? () => onUseImageAsVisualUniverseSeed(item.image)
                                  : undefined
                              }
                              onSave={() => saveImageToLibrary(item.image, false)}
                              onSaveAsReference={() => saveImageToLibrary(item.image, true)}
                            />
                          );
                        }

                        return (
                          <ChatGeneratedPostAssetVariantCard
                            key={item.key}
                            asset={item.asset}
                            profileLabel={profileLabel}
                            logoAssets={logoAssets}
                            isPrimary={item.asset.generatedAssetId === item.primaryGeneratedAssetId}
                            variantIndex={item.variantIndex}
                            variantCount={item.variantCount}
                            approvePending={
                              approvePost.isPending &&
                              approvePost.variables?.scheduledPostId === item.asset.scheduledPostId
                            }
                            rejectPending={
                              rejectPost.isPending &&
                              rejectPost.variables?.scheduledPostId === item.asset.scheduledPostId
                            }
                            makePrimaryPending={
                              primaryAssetPendingId === item.asset.generatedAssetId
                            }
                            onEdit={() => openAsset(item.asset)}
                            onOpenCampaign={() =>
                              navigate(buildBrandPath(brandId, `/campaigns/${item.asset.batchId}`))
                            }
                            onUseInChat={() => onUseAssetInChat?.(item.asset)}
                            onChangeCopyInChat={() => onChangeAssetCopyInChat?.(item.asset)}
                            onMakePrimary={() => useAssetAsMainImage(item.asset)}
                            onApprove={() => reviewAsset(item.asset, "approved")}
                            onReject={() => reviewAsset(item.asset, "rejected")}
                          />
                        );
                      })}
                    </div>
                  ))}
                </div>
              ) : (
                <MediaGalleryEmptyState>
                  <FormattedMessage id="pulse.mediaGallery.empty" defaultMessage="No media yet" />
                </MediaGalleryEmptyState>
              )}
            </div>
          </>
        )}
      </aside>
    </>
  );
}

function buildRowFirstMediaGalleryColumns<T>(items: readonly T[]) {
  const columns = Array.from({ length: INLINE_MEDIA_GALLERY_COLUMN_COUNT }, () => [] as T[]);

  items.forEach((item, index) => {
    columns[index % INLINE_MEDIA_GALLERY_COLUMN_COUNT]?.push(item);
  });

  return columns;
}

function MediaGalleryLoadingState() {
  return (
    <div className="space-y-3" aria-hidden="true">
      {Array.from({ length: 2 }).map((_, index) => (
        <div key={index} className="overflow-hidden rounded-3xl bg-background/60">
          <div className="space-y-2 p-3">
            <div className="h-3 w-2/3 animate-pulse rounded-full bg-muted" />
            <div className="grid grid-cols-3 gap-1.5">
              <div className="h-11 animate-pulse rounded-2xl bg-muted/70" />
              <div className="h-11 animate-pulse rounded-2xl bg-muted/70" />
              <div className="h-11 animate-pulse rounded-2xl bg-muted/70" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2 p-2">
            <div className="aspect-[9/16] animate-pulse rounded-2xl bg-muted" />
            <div className="aspect-[9/16] animate-pulse rounded-2xl bg-muted" />
          </div>
        </div>
      ))}
    </div>
  );
}

function MediaGalleryEmptyState({
  children,
  tone = "default",
}: {
  children: ReactNode;
  tone?: "default" | "error";
}) {
  return (
    <div
      className={cn(
        "flex min-h-32 flex-col items-center justify-center rounded-3xl px-4 text-center text-sm leading-6 text-muted-foreground",
        tone === "error"
          ? "border border-dashed border-destructive/30 bg-destructive/5 text-destructive"
          : "border border-transparent bg-transparent",
      )}
    >
      <Images className="mb-2 h-5 w-5 opacity-40" aria-hidden />
      {children}
    </div>
  );
}
